import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { TextInput } from '../../components/TextInput';
import { RegistrationButton } from '../../components/RegistrationButton';
import { Section, SectionRow, SectionText } from '../../components/Section';
import { Typography } from '../../components/Typography';
import { CheckboxInput } from '../../components/CheckboxInput';
import { SelectInput } from '../../components/SelectInput';
import { Hero } from '../../components/Hero';
import { PROJECT_ADMIN_TEXT } from '../../constants/text';
import { countries, provinces, states } from '../../data';
import { PhoneNumberInput } from '../../components/PhoneNumberInput';
import { addFormListener } from '../../../script';
import './RegistrationForm.css';

const subregionMap = {
  Canada: {
    subregions: provinces,
    label: 'Province',
  },
  'United States': {
    subregions: states,
    label: 'State',
  },
};

const basicValidator = (inputRef) => inputRef.current?.checkValidity();

export const RegistrationForm = ({
  onSubmit,
  sendingData,
  submissionData,
  setRegistrationError,
  setSendingData,
  setDataSubmitted,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(submissionData?.country || null);

  const [copyEmail, setCopyEmail] = useState(false);
  const [checkForErrors, setCheckForErrors] = useState(false);
  const [errors, setErrors] = useState({});

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const contactEmailRef = useRef(null);
  const contactPhoneRef = useRef(null);
  const companyNameRef = useRef(null);
  const countryRef = useRef(null);
  const subregionRef = useRef(null);
  const projectAdminEmailRef = useRef(null);
  const sameAsAboveRef = useRef(null);

  const showSubregion = !!subregionMap[selectedCountry];

  const updateSelectedCountry = (event) => {
    setSelectedCountry(event.target.value);
  };

  const onEmailChange = (event) => {
    if (copyEmail) {
      projectAdminEmailRef.current.value = event.target.value;
    }
  };

  const onCheckCopyEmail = (event) => {
    const { checked } = event.target;
    setCopyEmail(checked);
    if (checked) {
      projectAdminEmailRef.current.value = contactEmailRef.current.value;
    } else {
      projectAdminEmailRef.current.value = '';
    }
  };

  const validateInputs = () => {
    setCheckForErrors(true);
  };

  useEffect(() => {
    if (submissionData) {
      const {
        firstName,
        lastName,
        contactEmail,
        contactPhone,
        companyName,
        country,
        subregion,
        projectAdminEmail,
        sameAsAbove,
      } = submissionData;

      firstNameRef.current.value = firstName;
      lastNameRef.current.value = lastName;
      contactEmailRef.current.value = contactEmail;
      contactPhoneRef.current.value = contactPhone;
      companyNameRef.current.value = companyName;
      countryRef.current.value = country;
      setSelectedCountry(country);
      if (subregion) {
        subregionRef.current.value = subregion;
      }
      projectAdminEmailRef.current.value = projectAdminEmail;
      sameAsAboveRef.current.checked = sameAsAbove;
      setCopyEmail(sameAsAbove);
    }
    // On Mount Only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addFormListener(setSendingData, setDataSubmitted, setRegistrationError);
  }, [setSendingData, setDataSubmitted, setRegistrationError]);

  const fields = useMemo(() => ([
    {
      name: 'firstName',
      ref: firstNameRef,
      errorMessage: 'This field is required.',
      validator: basicValidator,
    },
    {
      name: 'lastName',
      ref: lastNameRef,
      errorMessage: 'This field is required.',
      validator: basicValidator,
    },
    {
      name: 'contactEmail',
      ref: contactEmailRef,
      errorMessage: 'Entered email is invalid.',
      validator: basicValidator,
    },
    {
      name: 'contactPhone',
      ref: contactPhoneRef,
      errorMessage: 'Entered phone number is invalid.',
      validator: basicValidator,
    },
    {
      name: 'companyName',
      ref: companyNameRef,
      errorMessage: 'This field is required.',
      validator: basicValidator,
    },
    {
      name: 'country',
      ref: countryRef,
      errorMessage: 'This field is required.',
      validator: () => !!selectedCountry,
    },
    {
      name: 'subregion',
      ref: subregionRef,
      errorMessage: 'This field is required.',
      validator: () => !!(!showSubregion || subregionRef.current?.value),
    },
    {
      name: 'projectAdminEmail',
      ref: projectAdminEmailRef,
      errorMessage: 'Entered email is invalid.',
      validator: basicValidator,
    },
  ]), [selectedCountry, showSubregion]);

  useEffect(() => {
    if (checkForErrors) {
      const nextErrors = fields.reduce((acc, {
        name, ref, errorMessage, validator,
      }) => {
        const hasError = !validator(ref);
        if (hasError) {
          acc[name] = errorMessage;
        }
        return acc;
      }, {});
      setErrors(nextErrors);
    }
    setCheckForErrors(false);
  }, [fields, checkForErrors]);

  const handleSubmit = (e) => {
    e.preventDefault();

    onSubmit({
      firstName: firstNameRef.current?.value,
      lastName: lastNameRef.current?.value,
      contactEmail: contactEmailRef.current?.value,
      contactPhone: contactPhoneRef.current?.value,
      companyName: companyNameRef.current?.value,
      country: countryRef.current?.value,
      subregion: subregionRef.current?.value,
      projectAdminEmail: projectAdminEmailRef.current?.value,
      sameAsAbove: sameAsAboveRef.current?.checked,
    });
  };

  return (
    <>
      <Hero />
      <form
        className="form-signin"
        method="POST"
        encType="multipart/form-data"
        onSubmit={handleSubmit}
      >
        <Section title="Primary Contact">
          <SectionRow>
            <TextInput
              ref={firstNameRef}
              displayLabel="First Name"
              id="firstName"
              name="firstName"
              required
              error={errors.firstName}
            />
            <TextInput
              ref={lastNameRef}
              displayLabel="Last Name"
              id="lastName"
              name="lastName"
              required
              error={errors.lastName}
            />
          </SectionRow>
          <SectionRow>
            <TextInput
              ref={contactEmailRef}
              type="email"
              onChange={onEmailChange}
              displayLabel="Primary Contact email"
              id="contactEmail"
              name="contactEmail"
              required
              error={errors.contactEmail}
            />
          </SectionRow>
          <SectionRow>
            <PhoneNumberInput
              ref={contactPhoneRef}
              displayLabel="Phone Number"
              id="contactPhone"
              name="contactPhone"
              required
              error={errors.contactPhone}
            />
            <TextInput
              ref={companyNameRef}
              displayLabel="Company"
              id="companyName"
              name="companyName"
              required
              error={errors.companyName}
            />
          </SectionRow>
          <SectionRow>
            <SelectInput
              ref={countryRef}
              onChange={updateSelectedCountry}
              displayLabel="Country"
              id="country"
              name="country"
              required
              options={countries}
              error={errors.country}
            />
            {showSubregion ? (
              <SelectInput
                ref={subregionRef}
                displayLabel={subregionMap[selectedCountry]?.label}
                id="subregion"
                name="subregion"
                required
                options={subregionMap[selectedCountry]?.subregions}
                error={errors.subregion}
              />
            ) : (
              <div className="text-input-wrapper" />
            )}
          </SectionRow>
        </Section>
        <Section title="Project Admin">
          <SectionText>
            <Typography>{PROJECT_ADMIN_TEXT}</Typography>
          </SectionText>
          <SectionRow>
            <CheckboxInput
              ref={sameAsAboveRef}
              onChange={onCheckCopyEmail}
              displayLabel="Same as above"
              name="sameAsAbove"
            />
          </SectionRow>
          <SectionRow>
            <TextInput
              ref={projectAdminEmailRef}
              type="email"
              disabled={copyEmail}
              displayLabel="Project Admin Email"
              id="projectAdminEmail"
              name="projectAdminEmail"
              required
              error={errors.projectAdminEmail}
            />
          </SectionRow>
        </Section>
        <RegistrationButton
          onSubmitClick={validateInputs}
          sendingData={sendingData}
        />
      </form>
    </>
  );
};

RegistrationForm.propTypes = {
  onSubmit: PropTypes.func,
  setRegistrationError: PropTypes.func,
  sendingData: PropTypes.bool,
  setSendingData: PropTypes.func,
  setDataSubmitted: PropTypes.func,
  submissionData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    companyName: PropTypes.string,
    country: PropTypes.string,
    subregion: PropTypes.string,
    projectAdminEmail: PropTypes.string,
    sameAsAbove: PropTypes.bool,
  }),
};

RegistrationForm.defaultProps = {
  onSubmit: () => { },
  sendingData: false,
  setRegistrationError: () => { },
  setSendingData: () => { },
  setDataSubmitted: () => { },
  submissionData: null,
};
